<script setup>
import { computed } from 'vue';
import { format, parseISO } from 'date-fns';
import BarChart from 'shared/components/charts/BarChart.vue';

// Usado no painel de consumo de IA do cliente e no super admin, que nao tem o
// i18n do produto: os textos chegam prontos em `labels`.
const props = defineProps({
  // [{ date: 'YYYY-MM-DD', tokens, included_tokens, excess_tokens }]
  days: {
    type: Array,
    required: true,
  },
  dailyIncluded: {
    type: Number,
    default: 0,
  },
  // { included, excess, dailyIncluded, tokens, day, aria }
  labels: {
    type: Object,
    required: true,
  },
  locale: {
    type: String,
    default: 'pt-BR',
  },
});

// Azul do tema (--blue-9, igual no claro e no escuro), fixo porque o super
// admin nao carrega essa variavel. O tema nao tem laranja: e o orange-500.
const INCLUDED_COLOR = 'rgb(39, 129, 246)';
const EXCESS_COLOR = 'rgb(249, 115, 22)';
const LINE_COLOR = 'rgb(var(--slate-12))';
// Altura do grafico e margens internas padrao do BarChart do @chatwoot/viz;
// a linha da franquia e desenhada por cima usando a mesma escala.
const CHART_HEIGHT = 288;
const CHART_PADDING_TOP = 24;
const CHART_PADDING_BOTTOM = 48;
const Y_TICK_COUNT = 5;

const formatTokens = value =>
  new Intl.NumberFormat(props.locale).format(value || 0);

// Escala do eixo Y fixada aqui para a linha da franquia cair no ponto certo:
// passo "redondo" (1, 2, 5 x 10^n) e topo cobrindo a maior barra e a franquia.
const yScale = computed(() => {
  const maxValue = Math.max(
    ...props.days.map(day => day.tokens),
    props.dailyIncluded,
    1
  );
  const rough = maxValue / (Y_TICK_COUNT - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / magnitude;
  let niceFraction = 1;
  if (fraction >= 7.5) niceFraction = 10;
  else if (fraction >= 3.5) niceFraction = 5;
  else if (fraction >= 1.5) niceFraction = 2;
  const step = niceFraction * magnitude;
  return { step, max: Math.ceil((maxValue * 1.05) / step) * step };
});

const includedLineY = computed(() => {
  const plotHeight = CHART_HEIGHT - CHART_PADDING_TOP - CHART_PADDING_BOTTOM;
  return (
    CHART_PADDING_TOP +
    (1 - props.dailyIncluded / yScale.value.max) * plotHeight
  );
});

const chartData = computed(() => ({
  categories: props.days.map(day => format(parseISO(day.date), 'dd')),
  series: [
    {
      id: 'included',
      label: props.labels.included,
      color: INCLUDED_COLOR,
      data: props.days.map(day => day.included_tokens),
    },
    {
      id: 'excess',
      label: props.labels.excess,
      color: EXCESS_COLOR,
      data: props.days.map(day => day.excess_tokens),
    },
  ],
}));

const legendItems = computed(() => [
  { key: 'included', label: props.labels.included, color: INCLUDED_COLOR },
  { key: 'excess', label: props.labels.excess, color: EXCESS_COLOR },
]);
</script>

<template>
  <div class="flex flex-col gap-3">
    <ul
      class="flex flex-wrap items-center justify-end gap-4 m-0 p-0 list-none text-xs text-n-slate-11"
    >
      <li
        v-for="item in legendItems"
        :key="item.key"
        class="flex items-center gap-1.5"
      >
        <svg class="size-3" aria-hidden="true">
          <rect width="100%" height="100%" rx="2" :fill="item.color" />
        </svg>
        {{ item.label }}
      </li>
      <li v-if="dailyIncluded" class="flex items-center gap-1.5">
        <svg class="w-4 h-3" aria-hidden="true">
          <line
            x1="0"
            x2="100%"
            y1="50%"
            y2="50%"
            :stroke="LINE_COLOR"
            stroke-width="2"
            stroke-dasharray="4 3"
          />
        </svg>
        {{ labels.dailyIncluded }}
      </li>
    </ul>
    <!-- gap largo: os rotulos do eixo Y passam ~21px da borda esquerda do SVG -->
    <div class="flex items-stretch gap-7">
      <span
        class="self-center text-xs text-n-slate-11 [writing-mode:vertical-rl] rotate-180"
      >
        {{ labels.tokens }}
      </span>
      <div class="relative flex-1 min-w-0">
        <BarChart
          :data="chartData"
          :aria-label="labels.aria"
          :format-value="formatTokens"
          :height="CHART_HEIGHT"
          :y-domain="[0, yScale.max]"
          :y-step-size="yScale.step"
          stacked
        />
        <!-- start e largura repetem as margens esquerda (52px) e direita (20px) do BarChart -->
        <svg
          v-if="dailyIncluded"
          class="absolute top-0 h-72 start-[3.25rem] w-[calc(100%-4.5rem)] pointer-events-none overflow-visible"
          aria-hidden="true"
        >
          <line
            x1="0"
            x2="100%"
            :y1="includedLineY"
            :y2="includedLineY"
            :stroke="LINE_COLOR"
            stroke-width="2"
            stroke-dasharray="6 4"
          />
        </svg>
      </div>
    </div>
    <p class="m-0 text-xs text-center text-n-slate-11">
      {{ labels.day }}
    </p>
  </div>
</template>
